import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { ScreenLayout, FooterMeta, Spinner, PageHeader } from '../shared';
import { CreateIntegrationButton } from '../../../../components/molecules/IntegrationButton';
import { ServiceKeyWithMore } from '../../../../../integrations/factory';

import { ReactComponent as ContactPropertyImg } from '../../../../assets/images/features/contact-property.svg';
import { ReactComponent as ScheduleTourImg } from '../../../../assets/images/features/schedule-a-tour.svg';
import { ReactComponent as ChatImg } from '../../../../assets/images/features/chat.svg';
import { ReactComponent as PricesAvailImg } from '../../../../assets/images/features/prices-avail.svg';
import { ReactComponent as PhotosImg } from '../../../../assets/images/features/photos.svg';
import { ReactComponent as NeighborhoodImg } from '../../../../assets/images/features/neighborhood.svg';
import { ReactComponent as RequestTextImg } from '../../../../assets/images/features/request-text.svg';
import { isDedicated } from '../../../../../shared/styles';

const StyledHeaderDescription = styled.p`
  margin: 0;
  color: #232323;
  font-size: 0.8125rem;
  line-height: 1.4;
`;

const StyledBody = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow-y: auto;

  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const StyledList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const StyledRow = styled.li`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border-bottom: 1px solid #e9e7e4;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover,
  &:focus-visible {
    background: #f5f5f4;
  }
`;

const StyledIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2.25rem;
  height: 2.25rem;

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const StyledDetail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  flex: 1;
  min-width: 0;
`;

const StyledLabel = styled.span`
  font-size: 0.9375rem;
  font-weight: 600;
  color: #232323;
`;

const StyledDescription = styled.span`
  font-size: 0.8125rem;
  color: #57534e;
  line-height: 1.4;
`;

const StyledCaret = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const StyledFooter = styled.div`
  padding-top: 1rem;
  border-top: 1px solid #e8e9eb;
`;

function CaretRight() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6 12L10 8L6 4"
        stroke="#57534E"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

type FeatureRowProps = {
  label: string;
  description: string;
  Icon: React.ElementType;
  isLoading?: boolean;
  onClick?: () => void;
};

function FeatureRow({
  label,
  description,
  Icon,
  isLoading,
  onClick,
}: Readonly<FeatureRowProps>) {
  return (
    <StyledRow
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <StyledIcon>
        <Icon />
      </StyledIcon>
      <StyledDetail>
        <StyledLabel>{label}</StyledLabel>
        <StyledDescription>{description}</StyledDescription>
      </StyledDetail>
      <StyledCaret>
        {isLoading ? <Spinner size="1rem" color="#57534e" /> : <CaretRight />}
      </StyledCaret>
    </StyledRow>
  );
}

export function MorePage() {
  const { listing } = globalThis.xprops;
  const navigate = useNavigate();
  const IntegratedRow = useMemo(
    () => CreateIntegrationButton<FeatureRowProps>(FeatureRow),
    []
  );

  const allowClose = useMemo(
    () => !isDedicated(globalThis.xprops.renderAs),
    []
  );

  const handleBack = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const handleClose = useCallback(() => {
    globalThis.xprops?.onToggleMessenger?.();
  }, []);

  const features: {
    service: ServiceKeyWithMore;
    label: string;
    description: string;
    Icon: React.ElementType;
    fallback?: () => void;
  }[] = [
    {
      service: 'chat',
      label: 'Chat with a Resident',
      description:
        "Ask people who live at this community questions about what it's like to live here.",
      Icon: ChatImg,
      fallback: () => {
        navigate('/');
      },
    },
    {
      service: 'contact',
      label: 'Contact Property',
      description: 'Get in touch with the leasing staff at this community.',
      Icon: ContactPropertyImg,
      fallback: () => {
        navigate('/contact');
      },
    },
    {
      service: 'schedule',
      label: 'Schedule Tour',
      description:
        'Use the scheduling tool to select a day and time to see this community.',
      Icon: ScheduleTourImg,
      fallback: () => {
        navigate('/schedule');
      },
    },
    {
      service: 'availability',
      label: 'Prices/Availability',
      description:
        'See pricing and unit availability details for this community.',
      Icon: PricesAvailImg,
    },
    {
      service: 'photos',
      label: 'Photos',
      description: 'View a photo gallery of this community.',
      Icon: PhotosImg,
    },
    {
      service: 'neighborhood',
      label: 'Neighborhood',
      description: 'Explore the neighborhood of this community.',
      Icon: NeighborhoodImg,
    },
    {
      service: 'requestText',
      label: 'Request an SMS Text',
      description:
        'Get in touch with the leasing staff at this community via SMS text.',
      Icon: RequestTextImg,
    },
  ];

  return (
    <ScreenLayout
      header={
        <PageHeader
          title={listing.name}
          onBack={handleBack}
          onClose={allowClose ? handleClose : undefined}
        >
          <StyledHeaderDescription>
            Get in touch with or see more details about this community by
            choosing from the options below.
          </StyledHeaderDescription>
        </PageHeader>
      }
      footer={
        <StyledFooter>
          <FooterMeta />
        </StyledFooter>
      }
    >
      <StyledBody>
        <StyledList role="list">
          {features.map((feature) => (
            <IntegratedRow
              key={feature.service}
              service={feature.service}
              platform="more"
              label={feature.label}
              description={feature.description}
              Icon={feature.Icon}
              fallback={feature.fallback}
            />
          ))}
        </StyledList>
      </StyledBody>
    </ScreenLayout>
  );
}
